import { ref } from 'vue';

export interface ToastOptions {
  /** Auto-dismiss in ms; pass 0 to keep until dismissed manually. Default 4000. */
  duration?: number;
  severity?: 'info' | 'success' | 'warning' | 'danger';
  /** Where the toast renders. Default 'top-right'; 'center' overlays the viewport middle. */
  position?: 'top-right' | 'center';
}

export interface ToastMessage extends ToastOptions {
  id: string;
  message: string;
  createdAt: number;
}

// Module-level state — single shared toast queue so any component (e.g., a top-level <ToastList/>)
// can render it without prop-drilling.
const toasts = ref<ToastMessage[]>([]);

let counter = 0;
const nextId = () => `toast-${Date.now()}-${++counter}`;

export const showToast = (message: string, options: ToastOptions = {}): string => {
  const id = nextId();
  const duration = options.duration ?? 4_000;
  toasts.value = [
    ...toasts.value,
    { id, message, severity: options.severity ?? 'info', duration, position: options.position ?? 'top-right', createdAt: Date.now() },
  ];
  if (duration > 0) {
    setTimeout(() => dismissToast(id), duration);
  }
  return id;
};

export const dismissToast = (id: string): void => {
  toasts.value = toasts.value.filter((t) => t.id !== id);
};

export const useToast = () => ({
  toasts,
  showToast,
  dismissToast,
});
